import {
  type Granularity,
  type LaneBar,
  type LaneItem,
  type Zoom,
  buildLanes,
  lanesWindow,
  panAnchor,
} from "@ondetatu/shared";
import { type ReactNode, useMemo, useState } from "react";
import "./Lanes.css";

const DAY = 86_400_000;
const dayMs = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
const todayIso = () => new Date().toISOString().slice(0, 10);
const MONTH = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PALETTE = ["#0e7c7b", "#e1603f", "#f2a541", "#7b5ea7", "#3a8e5f", "#3d6ea5", "#b5524e"];
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length] as string;
}
const initial = (name: string) => (name.trim()[0] ?? "?").toUpperCase();

const ZOOMS: Zoom[] = ["week", "month", "quarter", "year"];
const ROW_H = 26;

/** Greedy stacking: each bar takes the lowest row whose previous bar already ended. */
function assignRows(bars: LaneBar[]): { rows: (LaneBar & { row: number })[]; rowCount: number } {
  const ends: number[] = [];
  const sorted = [...bars].sort((a, b) => a.startFrac - b.startFrac);
  const rows = sorted.map((b) => {
    let r = 0;
    while (r < ends.length && (ends[r] as number) > b.startFrac + 1e-9) r++;
    ends[r] = b.endFrac;
    return { ...b, row: r };
  });
  return { rows, rowCount: Math.max(ends.length, 1) };
}

function ticksFor(win: { start: string; end: string }, zoom: Zoom) {
  const s = dayMs(win.start);
  const e = dayMs(win.end) + DAY;
  const span = e - s;
  const ticks: { frac: number; label: string }[] = [];
  if (zoom === "year") {
    const y = +win.start.slice(0, 4);
    for (let m = 0; m < 12; m++) {
      ticks.push({ frac: (Date.UTC(y, m, 1) - s) / span, label: MONTH[m] as string });
    }
  } else if (zoom === "week") {
    for (let t = s; t < e; t += DAY) {
      ticks.push({ frac: (t - s) / span, label: String(new Date(t).getUTCDate()) });
    }
  } else {
    const step = zoom === "month" ? 7 : 14;
    for (let t = s; t < e; t += step * DAY) {
      const d = new Date(t);
      ticks.push({ frac: (t - s) / span, label: `${MON[d.getUTCMonth()]} ${d.getUTCDate()}` });
    }
  }
  return ticks;
}

export function Lanes({
  items,
  title,
  scope,
}: {
  items: LaneItem[];
  title?: string;
  scope?: ReactNode;
}) {
  const [granularity, setGranularity] = useState<Granularity>("country");
  const [zoom, setZoom] = useState<Zoom>("month");
  const [anchor, setAnchor] = useState(todayIso());
  const [selected, setSelected] = useState<{ bar: LaneBar; lane: string } | null>(null);

  const window = useMemo(() => lanesWindow(zoom, anchor), [zoom, anchor]);
  const lanes = useMemo(
    () => buildLanes(items, { granularity, window }),
    [items, granularity, window],
  );
  const ticks = useMemo(() => ticksFor(window, zoom), [window, zoom]);
  const todayFrac = useMemo(() => {
    const s = dayMs(window.start);
    const f = (dayMs(todayIso()) - s) / (dayMs(window.end) + DAY - s);
    return f >= 0 && f <= 1 ? f : null;
  }, [window]);
  const rangeLabel = `${MON[+window.start.slice(5, 7) - 1]} ${+window.start.slice(8)} – ${MON[+window.end.slice(5, 7) - 1]} ${+window.end.slice(8)}`;

  return (
    <section className="ln">
      <div className="ln__head">
        {title && <h1 className="ln__h1">{title}</h1>}
        {scope}
      </div>

      <div className="ln__controls">
        <fieldset className="seg" aria-label="lane grouping">
          {(["country", "city"] as Granularity[]).map((g) => (
            <button
              key={g}
              type="button"
              className={granularity === g ? "on" : ""}
              onClick={() => setGranularity(g)}
            >
              {g === "country" ? "Countries" : "Cities"}
            </button>
          ))}
        </fieldset>
        <fieldset className="seg seg--zoom" aria-label="zoom">
          {ZOOMS.map((z) => (
            <button
              key={z}
              type="button"
              className={zoom === z ? "on" : ""}
              onClick={() => setZoom(z)}
            >
              {z[0]?.toUpperCase()}
              {z.slice(1)}
            </button>
          ))}
        </fieldset>
      </div>

      <div className="ln__nav">
        <button
          type="button"
          aria-label="previous"
          onClick={() => setAnchor((a) => panAnchor(zoom, a, -1))}
        >
          ‹
        </button>
        <span className="ln__range">{rangeLabel}</span>
        <button
          type="button"
          aria-label="next"
          onClick={() => setAnchor((a) => panAnchor(zoom, a, 1))}
        >
          ›
        </button>
        {anchor !== todayIso() && (
          <button type="button" className="ln__today-btn" onClick={() => setAnchor(todayIso())}>
            Today
          </button>
        )}
      </div>

      <div className="ln__board">
        <div className="ln__axis">
          <div className="ln__corner">place</div>
          <div className="ln__ticks">
            {ticks.map((t) => (
              <span
                key={`${t.label}-${t.frac}`}
                className="ln__tick"
                style={{ left: `${t.frac * 100}%` }}
              >
                <span>{t.label}</span>
              </span>
            ))}
            {todayFrac != null && (
              <span className="ln__today-tick" style={{ left: `${todayFrac * 100}%` }}>
                <span>now</span>
              </span>
            )}
          </div>
        </div>

        {lanes.map((lane) => {
          const { rows, rowCount } = assignRows(lane.bars);
          return (
            <div key={lane.key} className="ln__lane">
              <div className="ln__label">
                <span className="ln__place">{lane.label}</span>
                <span className="ln__cnt">
                  {lane.peopleCount} {lane.peopleCount === 1 ? "person" : "people"}
                </span>
              </div>
              <div className="ln__track" style={{ height: `${rowCount * ROW_H + 18}px` }}>
                {todayFrac != null && (
                  <span className="ln__now" style={{ left: `${todayFrac * 100}%` }} />
                )}
                {lane.overlaps.map((ov) => (
                  <span
                    key={`${ov.startFrac}-${ov.endFrac}`}
                    className="ln__overlap"
                    style={{
                      left: `${ov.startFrac * 100}%`,
                      width: `${(ov.endFrac - ov.startFrac) * 100}%`,
                    }}
                  >
                    <span className="ln__together">✨ {ov.count} here</span>
                  </span>
                ))}
                {rows.map((bar) => (
                  <button
                    key={bar.id}
                    type="button"
                    className={`ln__bar${bar.continuesLeft ? " cont-l" : ""}${bar.continuesRight ? " cont-r" : ""}`}
                    style={{
                      left: `${bar.startFrac * 100}%`,
                      width: `${(bar.endFrac - bar.startFrac) * 100}%`,
                      background: colorFor(bar.person_id),
                      top: `${bar.row * ROW_H + 14}px`,
                    }}
                    onClick={() => setSelected({ bar, lane: lane.label })}
                  >
                    <span className="ln__av">{initial(bar.person_name)}</span>
                    <span className="ln__bar-name">{bar.person_name}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {lanes.length === 0 && <p className="ln__empty">No travel to show in this window.</p>}
      </div>

      <p className="ln__hint">Lanes are places · bars are people · ✨ = your people together.</p>

      {selected && (
        <dialog open className="ln__sheet" aria-label="trip detail">
          <button
            type="button"
            className="ln__sheet-x"
            aria-label="close"
            onClick={() => setSelected(null)}
          >
            ×
          </button>
          <strong>{selected.bar.person_name}</strong>
          <span className="ln__sheet-where">
            {selected.bar.place} · {selected.lane}
          </span>
        </dialog>
      )}
    </section>
  );
}
